import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { omit } from 'ramda';

import { Formik, Form } from 'formik';
import { Grid, Container, Stepper, Step, StepButton } from '@material-ui/core';
import Button from '../../../components/Button';

import BasicInfo from './steps/BasicInfo';
import ConfigRPS from './steps/ConfigRps';
import ProvisionalReceiptInfo from './steps/ProvisionalReceiptInfo';
import ProvisionalReceiptFilter from './steps/ProvisionalReceiptFilter';

import TermsOfUseModal from './TermsOfUseModal';

import { setProvisionalReceiptInfo, registerFieldChange } from '../store/actions';
import { loadTermsOfUseUsers, loadTermsOfUseByEstablishment, loadProvisionalReceiptInfo, createRps, upsertRPS, updateCertificate, upsertConfigRps, updateFilters } from '../store/thunk';

import { basicInfoValidations } from './steps/BasicInfo/validations';
import { configRpsValidations } from './steps/ConfigRps/validations';
import { authenticationValidations } from './steps/ProvisionalReceiptInfo/validations';

import { formatToOnlyNumbersAndLetters } from 'helpers/formatters';

import { useStyles } from './styles';

export default function ProvisionalReceiptConfig() {
  const dispatch = useDispatch();

  const classes = useStyles();

  const history = useHistory();

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const businessLoading = useSelector(state => state.businessInfo.isLoading);
  const { isAdminUser } = useSelector(state => state.profile.submit);

  const {
    isLoading,
    isCityLoaded,
    termsOfUseUsers,
    acceptanceDateTime,
    certificateFileFromAPI,
    configType,
    isRpsConfig,
    custom_settings,
    logoFile,
    basicInfo,
    rpsConfig,
    rpsFilter,
    certificate,
    certificateFile,
    certificate: certificateData,
    login: loginData,
    token: tokenData,
    basicInfo: {
      sequence
    },
    authentication: {
      certificate: certificateBoolean,
      login: loginBoolean,
      password: passwordBoolean,
      sequence: citySequence
    }
  } = useSelector(state => state.provisionalReceiptConfig);

  const isCertificate = !!certificateBoolean;
  const isLogin = !!loginBoolean;
  const isPassword = !!passwordBoolean;

  const isUserAdmin = +isAdminUser == 1;

  const isNfTermsAccepted = termsOfUseUsers
    ?.some(user => user?.acceptanceDateTime != null && user?.acceptanceDateTime != '' && user?.acceptanceDateTime != '0001-01-01 00:00:00');

  const [isTermsOfUseModalOpen, setIsTermsOfUseModalOpen] = useState(false);
  const [isTermsOfUseModalAutoDismissed, setIsTermsOfUseModalAutoDismissed] = useState(false);
  const [isTermsLoaded, setIsTermsLoaded] = useState(!isUserAdmin);

  const [currentIndex, setCurrentIndex] = useState(0);

  const configurations = [
    {
      index: 0,
      id: 'authentication',
      title: 'Seleção de município',
      component: ProvisionalReceiptInfo,
      validations: (+configType == 0) ? null : authenticationValidations
    },
    {
      index: 1,
      id: 'basicInfo',
      title: 'Informações básicas',
      component: BasicInfo,
      validations: (+configType == 0) ? null : basicInfoValidations
    },
    ...(sequence || citySequence) ? (
      [{
        index: 2,
        id: 'configRps',
        title: 'Configuração RPS',
        component: ConfigRPS,
        validations: (+configType == 0) ? null : configRpsValidations
      }]
    ) : (
      []
    ),
    {
      index: (sequence || citySequence) ? 3 : 2,
      id: 'rpsInfo',
      title: 'Filtro de envio',
      component: ProvisionalReceiptFilter,
      validations: null
    }
  ];

  const currentConfiguration = configurations?.find(config => config.index == currentIndex) || {};
  const isBasicConfig = +currentConfiguration?.id == 1;

  useEffect(() => {
    if(establishmentId) {
      setCurrentIndex(0);
      setIsTermsLoaded(false);
      setIsTermsOfUseModalOpen(false);
      setIsTermsOfUseModalAutoDismissed(false);

      dispatch(loadProvisionalReceiptInfo(userId, establishmentId));

      if(!isUserAdmin) {
        dispatch(loadTermsOfUseByEstablishment(userId, establishmentId)).then(() => {
          setIsTermsLoaded(true);
        });
      }
    }
  }, [establishmentId, isUserAdmin]);

  useEffect(() => {
    if(isUserAdmin) {
      if(establishmentId) {
        dispatch(loadTermsOfUseUsers(userId, establishmentId))
          .then(() => setIsTermsLoaded(true));
      }
    }
  }, [establishmentId]);

  const isAutoTermsOfUseModalOpen = isTermsLoaded && !isTermsOfUseModalAutoDismissed && (!isUserAdmin && acceptanceDateTime != null && !acceptanceDateTime);

  const handleTermsOfUseModalOpen = () => {
    setIsTermsOfUseModalOpen(true);
  }

  const handleTermsOfUseModalState = isOpen => {
    if(isOpen) {
      setIsTermsOfUseModalOpen(true);
      return;
    }

    if(isTermsOfUseModalOpen) {
      setIsTermsOfUseModalOpen(false);
      return;
    }

    if(isUserAdmin) {
      setIsTermsOfUseModalAutoDismissed(true);
    }
  }

  const handlePreviousStep = () => {
    if(currentIndex == 0) {
      return history.push('/notas-fiscais');
    }

    setCurrentIndex(currentIndex - 1);
  }

  const handleNextStep = () => {
    if(currentIndex == configurations[configurations.length - 1].index) {
      return;
    }

    setCurrentIndex(currentIndex + 1);
  }

  const handleSubmit = values => {
    if(currentConfiguration.id == 'authentication') {
      if(+configType == 0) {
        return handleNextStep();
      }

      dispatch(registerFieldChange('cityCode', values?.cityCode));

      if(!values?.cityCode) {
        return toast.error('Escolha um município para continuar.');
      }

      if(isCertificate) {
        if(values?.certificateName && !values?.certificateFile) {
          return handleNextStep();
        }

        if(!certificateFileFromAPI) {
          if(!values?.certificateFile) {
            return toast.error('Escolha um certificado para continuar.');
          }
        }

        if(values?.certificateFile) {
          if(!values?.autenticatorPassword && !values?.autenticatorPasswordConfirm) {
            return handleNextStep();
          }

          const params = {
            userId,
            establishmentId,
            certificateFile: values?.certificateFile,
            autenticatorPassword: values?.autenticatorPassword,
            autenticatorPasswordConfirm: values?.autenticatorPasswordConfirm
          }

          dispatch(updateCertificate(params))
            .then(() => handleNextStep());
        }
      }

      if(!isCertificate) {
        return handleNextStep();
      }
    }

    if(currentConfiguration.id == 'basicInfo') {
      if(+configType == 0) {
        return handleNextStep();
      }

      let params = {
        logo: logoFile,
        fantasyName: values?.fantasyName,
        companyName: values?.companyName,
        documentId: formatToOnlyNumbersAndLetters(values?.documentId),
        phone: values?.phone,
        email: values?.email,
        postalCode: values?.postalCode,
        street: values?.street,
        number: values?.number,
        complement: values?.complement,
        district: values?.district,
        city: values?.city,
        state: values?.state,
        cityCode: values?.cityCode,
        municipalSubscription: values?.municipalSubscription,
        stateSubscription: values?.stateSubscription,
        taxationRegime: values?.taxationRegime,
        taxationRegimeSpecial: values?.taxationRegimeSpecial,
        encouraging: values?.encouraging,
        simpleNational: values?.simpleNational,
        streetType: values?.streetType,
        taxIncentive: values?.taxIncentive,
        nfse: {
          nfseNational: values?.nfseNational,
          serviceCode: values?.serviceCode,
          taxCode: values?.serviceItem,
          description: values?.description,
          taxType: values?.taxType,
          code: values?.code,
          aliquot: values?.aliquot?.replaceAll(',', '.'),
          issZeroAmount: values?.issZeroAmount,
          issChargeability: values?.issChargeability,
          issRetain: values?.issRetain,
          taxationNature: +values?.taxationNature == 0 ? '' : values?.taxationNature,
          taxationApurationNumber: +values?.taxationApurationNumber == 0 ? '' : values?.taxationApurationNumber,
          nationalAttachment: values?.nationalAttachment,
          nationalAliquot: values?.nationalAliquot,
          nbsCode: values?.nbsCode,
          ibscbsCode: values?.ibscbsCode,
          ibscbsCctCode: values?.ibscbsCctCode,
          percentualEstadual: values?.percentualEstadualValue,
          percentualMunicipal: values?.percentualMunicipalValue,
          percentualCbs: values?.percentualCbsValue
        }
      }

      if(isCertificate) {
        params = {
          ...params,
          ...(basicInfo?.certificateId != certificateData?.certificateId) && certificateData
        }
      }

      if(isCertificate && isLogin && !isPassword) {
        params = {
          ...params,
          nfse: {
            ...params.nfse,
            login: values?.autenticatorUser
          }
        }
      }

      if(isLogin || isPassword) {
        params = {
          ...params,
          nfse: {
            ...params.nfse,
            login: values?.autenticatorUser,
            password: values?.autenticatorUserPassword
          }
        }
      }

      if(!isLogin && isPassword) {
        params = {
          ...params,
          nfse: {
            ...params.nfse,
            token: values?.autenticatorToken
          }
        }
      }

      if(isRpsConfig) {
        return dispatch(upsertRPS(userId, establishmentId, params))
          .then(() => {
            dispatch(setProvisionalReceiptInfo({ ...params, ...params.nfse, ...certificate }));
            return handleNextStep();
          });
      }

      return dispatch(createRps(userId, establishmentId, params))
        .then(() => {
          dispatch(setProvisionalReceiptInfo({ ...params, ...params.nfse, ...certificate }));
          return handleNextStep();
        });
    }

    if(currentConfiguration.id == 'configRps') {
      if(+configType == 0) {
        return handleNextStep();
      }

      const params = {
        ...values,
        rpsGenerateOnline: 1
      }

      return dispatch(upsertConfigRps(userId, establishmentId, params))
        .then(() => handleNextStep());
    }

    if(currentConfiguration.id == 'rpsInfo') {
      const {
        statusRps,
        automaticSendNfse
      } = values;

      const {
        invoices,
        serviceOrders,
        selectedTransactions,
        paymentMethods
      } = values.automaticGenerate;

      const params = {
        taxSettingsUuid: basicInfo?.uuid,
        statusRps: !!statusRps ? 1 : 0,
        automaticSendNfse: !!automaticSendNfse ? 1 : 0,
        automaticGenerateNfse: JSON.stringify({
          invoices: !!invoices ? 1 : 0,
          serviceOrders: !!serviceOrders ? 1 : 0,
          selectedTransactions: !!selectedTransactions ? 1 : 0,
          paymentMethods: +serviceOrders == 0 ? paymentMethods : []
        })
      }

      return dispatch(updateFilters(userId, establishmentId, params))
        .then(() => history.push('/notas-fiscais'));
    }
  }

  const getInitialValues = () => {
    if(currentConfiguration.id == 'authentication') {
      return {
        ...certificateData,
        certificateFile,
        isCertificate,
        autenticatorPassword: '',
        autenticatorPasswordConfirm: '',
        cityCode: basicInfo?.cityCode || '',
        documentId: basicInfo?.documentId
      }
    }

    if(currentConfiguration.id == 'basicInfo') {
      return {
        ...omit(['nationalAttachment', 'nationalAliquot'], custom_settings || {}),
        ...basicInfo,
        ...tokenData,
        ...loginData,
        custom_settings,
        isLogin,
        isPassword,
        isUserAdmin
      }
    }

    if(currentConfiguration.id == 'configRps') {
      return rpsConfig;
    }

    if(currentConfiguration.id == 'rpsInfo') {
      return rpsFilter;
    }

    return {}
  }

  return(
    <Grid className={classes.root}>
      {(isTermsOfUseModalOpen || isAutoTermsOfUseModalOpen) && (
        <TermsOfUseModal
          isDismissable={isUserAdmin || isTermsOfUseModalOpen}
          isTermsAccepted={isUserAdmin ? isNfTermsAccepted : !!acceptanceDateTime}
          isOpen={isTermsOfUseModalOpen || isAutoTermsOfUseModalOpen}
          setIsOpen={handleTermsOfUseModalState}
        />
      )}
      <Stepper
        alternativeLabel
        nonLinear
        activeStep={currentIndex}
        style={{ background: 'transparent', padding: 0, paddingBottom: 24 }}
      >
        {configurations.map((nav, index) => (
          <Step key={nav.path}>
            <StepButton
              disabled={!isRpsConfig}
              completed={currentIndex > index}
              onClick={() => setCurrentIndex(index)}
            >
              {nav.title}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      <Grid>
        <Formik
          enableReinitialize
          initialValues={getInitialValues()}
          validationSchema={currentConfiguration.validations}
          validateOnChange={false}
          onSubmit={handleSubmit}
        >
          {({ ...formikProps }) => {
            return(
              <Form>
                {(currentIndex == currentConfiguration.index) && (
                  <currentConfiguration.component
                    formikProps={formikProps}
                    onOpenTermsOfUseModal={handleTermsOfUseModalOpen}
                  />
                )}
                <Grid container className={classes.footer}>
                  <Container>
                    <Grid container justify="space-between">
                      <Button
                        loading={isLoading || businessLoading}
                        type="button"
                        color="tertiary"
                        variant="outlined"
                        onClick={handlePreviousStep}
                      >
                        {+currentIndex == 0 ? 'Cancelar' : 'Voltar'}
                      </Button>
                      <Button
                        disabled={isBasicConfig && !isCityLoaded}
                        loading={isLoading || businessLoading}
                        color="success"
                        type="submit"
                      >
                        Avançar
                      </Button>
                    </Grid>
                  </Container>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Grid>
    </Grid>
  );
}